import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import api from '../utils/api';

const AuthContext = createContext({});

// Use relative URL - goes through nginx proxy
const API = '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize authentication state from localStorage
  useEffect(() => {
    const initAuth = () => {
      console.log('🔄 AuthContext initializing...');
      try {
        const storedUser = localStorage.getItem('exled_user');
        const storedToken = localStorage.getItem('exled_token');

        console.log('📦 Found stored data:', { storedUser: !!storedUser, storedToken: !!storedToken });

        if (storedUser && storedToken) {
          const userData = JSON.parse(storedUser);
          console.log('👤 Parsed user data:', userData);
          setUser(userData);
          console.log('✅ User set from localStorage');

          // Set axios default headers immediately on load
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

          // Verify token is still valid (with delay to allow backend to start)
          setTimeout(() => {
            console.log('🔍 Verifying token...');
            verifyToken(storedToken);
          }, 1000);
        } else {
          console.log('ℹ️  No stored auth data found');
        }
      } catch (error) {
        console.error('❌ Error loading stored auth:', error);
        // Clear invalid stored data
        localStorage.removeItem('exled_user');
        localStorage.removeItem('exled_token');
        localStorage.removeItem('exled_username');
        localStorage.removeItem('exled_password');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await axios.post(`${API}/auth/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.data.valid) {
        logout();
      }
    } catch (error) {
      // Try to refresh token if expired
      if (error.response && error.response.status === 401) {
        try {
          const refreshResponse = await axios.post(`${API}/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (refreshResponse.data.access_token) {
            // Update stored token
            localStorage.setItem('exled_token', refreshResponse.data.access_token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${refreshResponse.data.access_token}`;
            console.log('Token refreshed successfully');
            return;
          }
        } catch (refreshError) {
          console.log('Token refresh failed, logging out');
        }
        logout();
      } else {
        console.warn('Token verification failed due to network error, keeping user logged in');
      }
    }
  };

  const login = async (username, password) => {
    try {

      // Input validation
      if (!username || !password) {
        throw new Error('Username and password are required');
      }

      if (username.length < 3 || password.length < 6) {
        throw new Error('Invalid credentials format');
      }

      const response = await axios.post(`${API}/auth/login`, {
        username: username.trim(),
        password: password
      });

      const responseData = response.data;

      // Handle different response formats
      let user, token;
      if (responseData.success !== undefined) {
        // New format: { success: true, user: {...}, token: "..." }
        user = responseData.user;
        token = responseData.token;
      } else {
        // Old format: { user: {...}, token: "..." }
        user = responseData.user;
        token = responseData.token;
      }

      // Store user and token
      localStorage.setItem('exled_user', JSON.stringify(user));
      localStorage.setItem('exled_token', token);
      localStorage.setItem('exled_username', username.trim());
      localStorage.setItem('exled_password', password);

      // Set axios default headers immediately
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setUser(user);
      return true;

    } catch (error) {
      console.error('Login error:', error);

      // For development only - remove in production
      // Disabled now that we have real admin user
      // if (process.env.NODE_ENV === 'development') {
      //   return await fallbackLogin(username, password);
      // }

      return false;
    }
  };

  // Temporary fallback for development - REMOVE IN PRODUCTION
  const fallbackLogin = async (username, password) => {
    const devUsers = {
      'admin': {
        id: 'admin-user-id',
        username: 'admin',
        email: 'admin@exled.co.uk',
        full_name: 'System Administrator',
        role: 'admin',
        status: 'active',
        departments: ['product_development', 'product_marketing', 'sales'],
        password: 'ExLed2025!Admin'
      }
    };

    const foundUser = devUsers[username.toLowerCase()];
    
    if (!foundUser || foundUser.password !== password) {
      return false;
    }

    // Remove password from user object before storing
    const { password: _, ...userWithoutPassword } = foundUser;
    const authToken = `dev-token-${Date.now()}`;

    // Store user and token
    localStorage.setItem('exled_user', JSON.stringify(userWithoutPassword));
    localStorage.setItem('exled_token', authToken);
    
    setUser(userWithoutPassword);
    return true;
  };

  const logout = () => {
    // Clear stored authentication data
    localStorage.removeItem('exled_user');
    localStorage.removeItem('exled_token');
    localStorage.removeItem('exled_username');
    localStorage.removeItem('exled_password');
    setUser(null);
  };

  const isAuthenticated = () => {
    return !!user;
  };

  const hasPermission = (permission) => {
    console.log('Checking permission:', permission, 'for user:', user); // Debug log
    if (!user) {
      console.log('No user found, permission denied');
      return false;
    }
    if (user.role === 'admin') {
      console.log('User is admin, permission granted');
      return true;
    }

    // Add more granular permission checking here as needed
    switch (permission) {
      case 'create_product':
        const createResult = user.can_add_products || user.role === 'contributor' || user.role === 'admin';
        console.log('Create product permission result:', createResult);
        return createResult;
      case 'update_product':
        const updateResult = user.role === 'contributor' || user.role === 'admin';
        console.log('Update product permission result:', updateResult);
        return updateResult;
      case 'delete_product':
        const deleteResult = user.can_delete_products || user.role === 'admin';
        console.log('Delete product permission result:', deleteResult, 'can_delete_products:', user.can_delete_products, 'role:', user.role);
        return deleteResult;
      case 'manage_users':
        return user.role === 'admin';
      case 'view_products':
      case 'send_messages':
        return true; // All authenticated users can view products and send messages
      default:
        return false;
    }
  };

  const getCurrentUser = () => {
    return user;
  };

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('exled_token');
      if (!token) return false;

      const response = await axios.post(`${API}/auth/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.valid && response.data.user) {
        // Update stored user data with fresh data from server
        localStorage.setItem('exled_user', JSON.stringify(response.data.user));
        setUser(response.data.user);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return false;
    }
  };

  // Update axios defaults when user changes
  useEffect(() => {
    const token = localStorage.getItem('exled_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [user]);

  const value = {
    user,
    login,
    logout,
    isAuthenticated,
    hasPermission,
    getCurrentUser,
    refreshUser,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};